import { Link } from 'react-router-dom';


function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 id={id} className="text-lg font-bold text-gray-900 mb-3 scroll-mt-24">{title}</h2>
      <div className="space-y-3 text-sm text-gray-600 leading-relaxed">{children}</div>
    </section>
  );
}

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-3">
          <Link to="/">
            <img src="/assets/logo/logo-full-dark-text.png" alt="badges.ninja" className="h-9" />
          </Link>
          <Link to="/signup" className="rounded-full bg-primary-600 px-5 py-2 text-xs font-semibold text-white hover:bg-primary-700">
            Sign up free
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
        <article>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">Privacy Policy</h1>
          <p className="mt-3 text-sm text-gray-500">Last updated: April 13, 2026</p>

          <Section id="overview" title="What Data Badges Ninja Collects and Why">
            <p>
              This Privacy Policy explains how <strong>BWS</strong> ("we", "us", or "our"), operating the badges.ninja platform, collects, uses, and protects your personal information when you use our digital badge and credential platform.
            </p>
            <p><strong>Account Information.</strong> When you create an account we collect your name, email address, and authentication credentials. If you sign in via Google or GitHub, we receive your profile name and email from the identity provider — we do not receive or store your social account passwords.</p>
            <p><strong>Badge Data.</strong> We collect and store data related to your use of the platform, including: issuer profiles you create (name, description, logo), badge definitions (title, description, criteria, image), and credential records (recipient details, issue date, evidence, status).</p>
            <p><strong>Recipient Data.</strong> When you issue a badge, we collect the recipient's name and email address. Recipient email addresses are SHA-256 hashed in publicly accessible badge JSON metadata to protect privacy. The plaintext email is stored only in our private database for notification and verification purposes.</p>
            <p><strong>Usage Data.</strong> We automatically collect information about how you interact with the service, including: badges created and issued, API key usage, access timestamps, and platform feature usage.</p>
            <p><strong>Payment Information.</strong> If you subscribe to a paid plan, payment processing is handled entirely by Stripe. We receive your subscription status and plan type but do not store credit card numbers or banking details.</p>
            <p><strong>Technical Data.</strong> We collect standard web server logs including IP addresses, browser type, and referring pages. AWS CloudFront and API Gateway generate these logs for operational purposes.</p>
          </Section>

          <Section id="how-we-use" title="How We Use Your Data">
            <p>We use your information to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Provide, maintain, and improve the badges.ninja platform</li>
              <li>Authenticate your identity and manage your account</li>
              <li>Issue, deliver, and verify digital badges on your behalf</li>
              <li>Send badge notification emails to recipients</li>
              <li>Process subscriptions and billing via Stripe</li>
              <li>Track usage and enforce plan limits</li>
              <li>Send transactional emails (verification codes, password resets, badge notifications)</li>
              <li>Monitor service health and prevent abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>We do not sell, rent, or share your personal information with third parties for marketing purposes.</p>
          </Section>

          <Section id="ipfs-storage" title="Badge Image Hosting">
            <p><strong>Where badge images are stored.</strong> Badge images are stored in Amazon S3 (AWS, US East — N. Virginia) and served through our CloudFront CDN. Each image is stored under a content-addressed, immutable URL derived from a cryptographic hash of the image.</p>
            <p><strong>Deletion.</strong> When you delete a badge or your account, we remove its image from our storage. Copies of images or verification pages you have already shared publicly (for example on social networks) are outside our control.</p>
            <p><strong>Public badge metadata.</strong> Badge verification pages and metadata are designed to be publicly accessible. This is fundamental to how digital credentials work — verifiers need to confirm that a badge is authentic. Public metadata includes the badge title, issuer name, issue date, and a hashed recipient email.</p>
          </Section>

          <Section id="recipient-privacy" title="Recipient Privacy">
            <p><strong>Email hashing.</strong> When a badge is issued, the recipient's email address is converted to a SHA-256 hash in the publicly accessible badge JSON. This allows the recipient to prove ownership of a badge without exposing their email address to the public.</p>
            <p><strong>Plaintext storage.</strong> The recipient's plaintext email is stored only in our private DynamoDB database. It is used to send badge notification emails and to allow recipients to claim and manage their badges.</p>
            <p><strong>Recipient rights.</strong> Badge recipients who do not have a badges.ninja account may contact us to request removal of their personal data from our systems.</p>
          </Section>

          <Section id="data-storage" title="Data Storage and Security">
            <p>Your account data, badge metadata, and issuer information are stored in AWS DynamoDB in the US East (N. Virginia) region. Badge images are stored in Amazon S3 in the same region and served via CloudFront.</p>
            <p>We implement industry-standard security measures including:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Encryption in transit (TLS/HTTPS for all connections)</li>
              <li>Authentication via AWS Cognito with JWT tokens</li>
              <li>API key authentication with hashed storage</li>
              <li>SHA-256 hashing of recipient emails in public metadata</li>
              <li>AWS IAM role-based access controls</li>
              <li>Secrets Manager for credential storage</li>
            </ul>
          </Section>

          <Section id="third-party" title="Third-Party Services">
            <p>We use the following third-party services that may process your data:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Amazon Web Services (AWS)</strong> — infrastructure hosting, authentication (Cognito), data storage (DynamoDB), content delivery (CloudFront), email delivery (SES)</li>
              <li><strong>Stripe</strong> — payment processing for paid subscriptions</li>
              <li><strong>Google</strong> — OAuth authentication (if you choose to sign in with Google)</li>
              <li><strong>GitHub</strong> — OAuth authentication (if you choose to sign in with GitHub)</li>
            </ul>
            <p>Each third-party service is governed by its own privacy policy.</p>
          </Section>

          <Section id="cookies" title="Cookies and Local Storage">
            <p>We use essential cookies and browser local storage for:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Authentication session tokens (AWS Cognito)</li>
              <li>Language preference storage</li>
              <li>CSRF protection during OAuth flows</li>
            </ul>
            <p>We do not use advertising or tracking cookies. We do not use any third-party analytics services.</p>
          </Section>

          <Section id="your-rights" title="Your Rights Under GDPR">
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Access</strong> your personal data stored in your account</li>
              <li><strong>Correct</strong> inaccurate information via your profile settings</li>
              <li><strong>Delete</strong> your account and associated data by contacting us</li>
              <li><strong>Export</strong> your badge data and metadata via the API</li>
              <li><strong>Withdraw consent</strong> for optional data processing at any time</li>
            </ul>
            <p>Note that deleting your account removes your data from our systems but cannot recall badge images or verification records that have already been publicly shared.</p>
          </Section>

          <Section id="data-retention" title="Data Retention and Deletion">
            <p>We retain your account data for as long as your account is active. If you delete your account, we remove your personal data within 30 days, except where retention is required by law.</p>
            <p>Badge metadata and credential records may be retained for verification purposes even after account deletion, as third parties may rely on the ability to verify previously issued credentials. Badge images are deleted from our storage upon account deletion.</p>
          </Section>

          <Section id="children" title="Children's Privacy">
            <p>badges.ninja is not directed at children under the age of 16. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us and we will delete it.</p>
          </Section>

          <Section id="changes" title="Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page with a revised "Last updated" date. Your continued use of the service after changes constitutes acceptance of the updated policy.</p>
          </Section>

          <Section id="contact" title="Contact">
            <p>For questions about this Privacy Policy or to exercise your rights, contact us at:</p>
            <p className="mt-2">
              <strong>BWS</strong><br />
              Email: <a href="mailto:info@bws.ninja" className="text-primary-600 hover:text-primary-500">info@bws.ninja</a><br />
              Website: <a href="https://www.bws.ninja" className="text-primary-600 hover:text-primary-500" target="_blank" rel="noopener noreferrer">www.bws.ninja</a>
            </p>
          </Section>
        </article>

        {/* Footer nav */}
        <div className="mt-16 pt-8 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-900">Back to badges.ninja</Link>
          <Link to="/terms-of-service" className="hover:text-gray-900">Terms of Service</Link>
        </div>
      </main>
    </div>
  );
}
